import type { ReactNode } from "react";

import { Alert, AlertDialog, Button, Modal } from "@heroui/react";
import { TriangleAlert } from "lucide-react";

/**
 * The short modal: 448px, one title, a body, Cancel and one primary action.
 * Pass `onSubmit` for a form (the footer's primary button submits it) or `onConfirm` for a plain action.
 */
export function FormModal({
  isOpen,
  onClose,
  title,
  children,
  submitLabel,
  isSubmitting,
  isSubmitDisabled,
  onSubmit,
  cancelLabel = "Cancel",
  nativeValidation = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  submitLabel: string;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  onSubmit: () => void;
  cancelLabel?: string;
  /** Let the browser validate `required` and `type="url"` fields on submit (forms without a zod schema). */
  nativeValidation?: boolean;
}) {
  return (
    <Modal.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && onClose()}
    >
      <Modal.Container placement="center">
        <Modal.Dialog className="w-full max-w-md">
          <form
            noValidate={!nativeValidation}
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading>{title}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-4 p-1">
              {children}
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onClose}>
                {cancelLabel}
              </Button>
              <Button
                isDisabled={isSubmitDisabled}
                isPending={isSubmitting}
                type="submit"
              >
                {submitLabel}
              </Button>
            </Modal.Footer>
          </form>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

/** The destructive confirm: an alert dialog whose primary action is the red one. */
export function ConfirmDialog({
  isOpen,
  onClose,
  title,
  children,
  confirmLabel,
  isPending,
  onConfirm,
  cancelLabel = "Cancel",
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  isPending?: boolean;
  onConfirm: () => void;
  cancelLabel?: string;
}) {
  return (
    <AlertDialog.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && onClose()}
    >
      <AlertDialog.Container>
        <AlertDialog.Dialog className="w-full max-w-md">
          <AlertDialog.Header>
            <AlertDialog.Icon status="danger">
              <TriangleAlert className="size-5" />
            </AlertDialog.Icon>
            <AlertDialog.Heading>{title}</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body>{children}</AlertDialog.Body>
          <AlertDialog.Footer>
            <Button variant="ghost" onPress={onClose}>
              {cancelLabel}
            </Button>
            <Button isPending={isPending} variant="danger" onPress={onConfirm}>
              {confirmLabel}
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}

/** The server's error message at the top of a form; renders nothing without one. */
export function FormError({ message }: { message?: string | null }) {
  if (!message) return null;

  return (
    <Alert status="danger">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{message}</Alert.Title>
      </Alert.Content>
    </Alert>
  );
}
